import type { QuestionResult } from '@/state/surveys/useSurveyResults';
import { cn } from 'helpers/cn';

type QuestionResultsProps = { questions: QuestionResult[] };

type QuestionCardProps = { question: QuestionResult; index: number };

const KIND_LABEL: Record<string, string> = {
  score: 'Score',
  'single choice': 'Single choice',
  'multiple choice': 'Multiple choice',
  'open text': 'Text',
};

const headlineCaption = (question: QuestionResult): string => {
  if (question.scale === 'NPS') return 'Net score';
  if (question.scale === 'CSAT') return 'Average, out of 5';
  return 'Average, out of 7';
};

/**
 * One row per value: its label, a bar for its share of the people who answered the
 * question, and the count and share as text. Plain HTML bars rather than a chart: a
 * handful of labelled rows is a table, read row by row, and every number is written
 * out, so nothing depends on reading a bar's length or colour.
 */
const QuestionCard = ({ question, index }: QuestionCardProps) => {
  const isScore = question.kind === 'score';
  const isText = question.kind === 'open text';

  return (
    <div className="bg-panel2 border-line rounded-card border p-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="micro-label m-0">
            {index + 1}. {KIND_LABEL[question.kind] ?? question.kind}
          </p>
          <p className="text-foreground m-0 mt-1 text-meta break-words">{question.label}</p>
        </div>
        {isScore && question.headline != null && (
          <div className="shrink-0 text-right">
            <p className="text-foreground num m-0 text-title">{question.headline}</p>
            <p className="micro-label m-0">{headlineCaption(question)}</p>
          </div>
        )}
      </div>

      <p className="supporting m-0 mt-2">
        {question.answered} {question.answered === 1 ? 'answer' : 'answers'}
        {question.kind === 'multiple choice' && question.answered > 0 && ', so shares can add up to more than 100%'}
      </p>

      {!isText && question.answered > 0 && (
        <ul className={cn('m-0 mt-3 flex list-none flex-col p-0', isScore ? 'gap-1' : 'gap-1.5')}>
          {question.counts.map(({ value, count }) => {
            const share = Math.round((count / question.answered) * 100);
            return (
              <li key={value} className="grid grid-cols-[minmax(0,8rem)_1fr_auto] items-center gap-2">
                <span className="text-foreground truncate text-micro" title={value}>
                  {value}
                </span>
                <span className="bg-field h-2 overflow-hidden rounded-full" aria-hidden="true">
                  <span className="bg-sel block h-full rounded-full" style={{ width: `${share}%` }} />
                </span>
                <span className="text-dim num text-micro">
                  {count} · {share}%
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {isText && question.answered > 0 && (
        <p className="supporting m-0 mt-2">Read them in the responses below, filtered to those with a comment.</p>
      )}
    </div>
  );
};

export const QuestionResults = ({ questions }: QuestionResultsProps) => (
  <section className="bg-panel border-line rounded-panel border" data-tour="survey-results-questions">
    <div className="border-line border-b px-4 py-3">
      <h2 className="text-foreground m-0 text-body font-medium">By question</h2>
    </div>
    <div className="grid gap-3 p-3 lg:grid-cols-2">
      {questions.map((question, index) => (
        <QuestionCard key={question.questionId} question={question} index={index} />
      ))}
    </div>
  </section>
);
